import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  RectangleStackIcon,
  PlusIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { useMetasCompraDimensao } from '../hooks/useMetaCompraDimensao';
import { useEmpresas } from '../hooks/usePesquisas';
import { usePermissoes } from '../hooks/usePermissoes';
import { money } from '../utils/format';
import ModalMetaCompraDimensao from '../components/metasCompraDimensao/ModalMetaCompraDimensao';
import MetaCompraDrillDownDrawer from '../components/metasCompra/MetaCompraDrillDownDrawer';
import AjudaTitulo from '../components/ui/AjudaTitulo';
import { inputIris } from '../components/ui/iris';
import MenuAcoes, { type AcaoMenu } from '../components/ui/MenuAcoes';
import SortHeader, { useSort } from '../components/ui/SortHeader';
import type {
  DimensaoTipo,
  MetaCompraDimensao,
  StatusFiltro,
} from '../types/metaCompraDimensao';

const STORAGE_EMPRESAS = 'metas.dim.empresas';
const STORAGE_ANOMES = 'metas.dim.anoMes';
const STORAGE_DIMENSAO = 'metas.dim.dimensao';

function anoMesAtual(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function corPct(pct: number): { bg: string; text: string; bar: string; label: string } {
  if (pct > 100) return { bg: 'bg-danger-soft', text: 'text-danger', bar: 'bg-danger', label: 'Acima' };
  if (pct >= 80) return { bg: 'bg-warning-soft', text: 'text-warning', bar: 'bg-warning', label: 'Alerta' };
  return { bg: 'bg-ok-soft', text: 'text-ok', bar: 'bg-ok', label: 'Folgado' };
}

function corDimensao(d: string): string {
  switch (d) {
    case 'fornecedor': return 'bg-accent-soft text-accent-text';
    case 'marca':      return 'bg-warning-soft text-warning';
    case 'grupo':      return 'bg-ok-soft text-ok';
    default:           return 'bg-surface-2 text-tx-2';
  }
}

const PAGE_SIZE = 50;

const MetasCompraDimensaoPage: React.FC = () => {
  const { temPermissao, isAdmin } = usePermissoes();
  // Audit Fase B (2026-05-21): granular por ação destrutiva/efetivadora.
  // `compras.metas` libera VER a tela; `compras.meta.gravar` libera criar/editar.
  const podeEditar = isAdmin || temPermissao('compras.meta.gravar');

  // Filtros persistidos
  const [empresasSel, setEmpresasSel] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_EMPRESAS);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch { return []; }
  });
  const [anoMes, setAnoMes] = useState<string>(() => localStorage.getItem(STORAGE_ANOMES) ?? anoMesAtual());
  const [dimensao, setDimensao] = useState<DimensaoTipo | ''>(() => {
    const v = localStorage.getItem(STORAGE_DIMENSAO);
    return (v === 'fornecedor' || v === 'marca' || v === 'grupo') ? v : '';
  });
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState<StatusFiltro | ''>('');
  const [page, setPage] = useState(1);
  const { sort, onSort } = useSort();

  useEffect(() => { localStorage.setItem(STORAGE_EMPRESAS, JSON.stringify(empresasSel)); }, [empresasSel]);
  useEffect(() => { localStorage.setItem(STORAGE_ANOMES, anoMes); }, [anoMes]);
  useEffect(() => { localStorage.setItem(STORAGE_DIMENSAO, dimensao); }, [dimensao]);

  // Reset página ao mudar filtros/ordenação (exceto page)
  useEffect(() => { setPage(1); }, [anoMes, dimensao, status, busca, empresasSel, sort]);

  const [drilldownAlvo, setDrilldownAlvo] = useState<{ dimensao: DimensaoTipo; codigo: string; titulo: string } | null>(null);
  const [modalAberta, setModalAberta] = useState(false);
  const [metaEditando, setMetaEditando] = useState<MetaCompraDimensao | null>(null);

  const { data: empresas = [] } = useEmpresas();

  const filtros = useMemo(() => ({
    anoMes,
    dimensao: dimensao || undefined,
    empresa: empresasSel.length > 0 ? empresasSel : undefined,
    busca: busca || undefined,
    status: status || undefined,
    page,
    pageSize: PAGE_SIZE,
    ordenarPor: sort?.campo,
    ordemDesc: sort?.desc,
  }), [anoMes, dimensao, empresasSel, busca, status, page, sort]);

  const { data, isLoading, isFetching, refetch } = useMetasCompraDimensao(filtros);

  const toggleEmpresa = (cod: string) => {
    setEmpresasSel((prev) => prev.includes(cod) ? prev.filter((e) => e !== cod) : [...prev, cod]);
  };

  const abrirCadastro = (meta?: MetaCompraDimensao) => {
    setMetaEditando(meta ?? null);
    setModalAberta(true);
  };

  const items = data?.items ?? [];
  const kpis = data?.kpis;
  const totalPages = data?.totalPages ?? 1;
  const totalCount = data?.totalCount ?? 0;
  const inicio = data ? (data.currentPage - 1) * data.pageSize + 1 : 0;
  const fim = data ? Math.min(data.currentPage * data.pageSize, data.totalCount) : 0;

  return (
    <div className="mx-auto flex h-full w-full max-w-[1280px] flex-col gap-3">
      {/* Page header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-[22px] font-bold text-tx tracking-[-0.02em]">
            <RectangleStackIcon className="h-6 w-6 flex-shrink-0 text-accent-text" aria-hidden />
            Metas por Dimensão
            <AjudaTitulo>
              Planejamento granular por <b>fornecedor</b>, <b>marca</b> ou <b>grupo</b> — coexiste com
              as Metas Mensais (consolidadas). Filtre por dimensão/status na barra abaixo; use
              <b>Cadastrar em lote</b> pra criar várias de uma vez com sugestões automáticas.
            </AjudaTitulo>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex h-8 items-center gap-1 rounded-iris border border-line px-2.5 text-xs text-tx-2 hover:bg-surface-2 transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          {podeEditar && (
            <Link
              to="/compras/metas/dimensao/lote"
              className="flex h-8 items-center gap-1 rounded-iris border border-accent bg-accent-soft px-3 text-xs font-medium text-accent-text hover:opacity-90 transition-opacity"
              title="Cadastrar várias metas de uma vez com sugestões automáticas"
            >
              <ClipboardDocumentListIcon className="h-3.5 w-3.5" />
              Cadastrar em lote
            </Link>
          )}
          {podeEditar && (
            <button
              type="button"
              onClick={() => abrirCadastro()}
              className="flex h-8 items-center gap-1 rounded-iris bg-accent px-3 text-xs font-medium text-white hover:opacity-90 transition-opacity"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Cadastrar meta
            </button>
          )}
        </div>
      </div>

      {/* KPIs agregados */}
      {kpis && (
        <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
          <div className="rounded-iris-lg border border-line border-l-4 border-l-line-strong bg-surface p-3 shadow-sm">
            <div className="text-[10px] uppercase tracking-wide text-tx-3">Total de metas</div>
            <div className="mt-1 text-xl font-bold text-tx">{kpis.totalMetas}</div>
          </div>
          <div className="rounded-iris-lg border border-line border-l-4 border-l-accent bg-surface p-3 shadow-sm">
            <div className="text-[10px] uppercase tracking-wide text-tx-3">Soma das metas</div>
            <div className="mt-1 text-xl font-bold text-tx">{money(kpis.somaMetas)}</div>
          </div>
          <div className="rounded-iris-lg border border-line border-l-4 border-l-line-strong bg-surface p-3 shadow-sm">
            <div className="text-[10px] uppercase tracking-wide text-tx-3">Saldo realizado</div>
            <div className="mt-1 text-xl font-bold text-tx">{money(kpis.somaRealizado)}</div>
            <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-tx-3">
              <span>Pedido: <span className="font-medium text-tx-2">{money(kpis.somaPedido)}</span></span>
              {kpis.somaCancelado > 0 && (
                <span>Cancelado: <span className="font-medium text-danger">−{money(kpis.somaCancelado)}</span></span>
              )}
            </div>
          </div>
          <div className="rounded-iris-lg border border-line border-l-4 border-l-line bg-surface p-3 shadow-sm">
            <div className="text-[10px] uppercase tracking-wide text-tx-3">Distribuição</div>
            <div className="mt-1 flex items-baseline gap-2 text-xs">
              <span className="text-ok">{kpis.qtdeAtingidas}</span>
              <span className="text-tx-3">/</span>
              <span className="text-warning">{kpis.qtdeAlerta}</span>
              <span className="text-tx-3">/</span>
              <span className="text-danger">{kpis.qtdeAcima}</span>
            </div>
            <div className="mt-0.5 text-[10px] text-tx-3">folgado / alerta / acima</div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-3 rounded-iris-lg border border-line bg-surface p-3 shadow-sm">
        <div className="flex flex-col">
          <label className="mb-1 text-[10px] uppercase tracking-wide text-tx-3">Mês</label>
          <input
            type="month"
            value={anoMes}
            onChange={(e) => setAnoMes(e.target.value)}
            className={`${inputIris} h-8 text-sm`}
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-1 text-[10px] uppercase tracking-wide text-tx-3">Dimensão</label>
          <div className="flex gap-1">
            {([['', 'Todas'], ['fornecedor', 'Fornecedor'], ['marca', 'Marca'], ['grupo', 'Grupo']] as const).map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setDimensao(v as DimensaoTipo | '')}
                className={`h-8 rounded-iris-sm border px-2 text-xs font-medium transition-colors ${
                  dimensao === v
                    ? 'border-tx bg-accent text-white'
                    : 'border-line bg-surface text-tx-2 hover:bg-surface-2'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col">
          <label className="mb-1 text-[10px] uppercase tracking-wide text-tx-3">Status</label>
          <div className="flex gap-1">
            {([['', 'Todos'], ['folgado', 'Folgado'], ['alerta', 'Alerta'], ['acima', 'Acima']] as const).map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setStatus(v as StatusFiltro | '')}
                className={`h-8 rounded-iris-sm border px-2 text-xs transition-colors ${
                  status === v
                    ? 'border-tx bg-accent text-white'
                    : 'border-line bg-surface text-tx-2 hover:bg-surface-2'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col flex-1 min-w-[180px]">
          <label className="mb-1 text-[10px] uppercase tracking-wide text-tx-3">Busca</label>
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-tx-3" />
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Código, nome ou descrição..."
              className={`w-full ${inputIris} h-8 pl-8 text-sm`}
            />
          </div>
        </div>
        <div className="flex-1 min-w-[240px]">
          <label className="mb-1 block text-[10px] uppercase tracking-wide text-tx-3">Empresas</label>
          <div className="flex flex-wrap gap-1">
            {empresas.map((e) => {
              const ativa = empresasSel.includes(e.codigo);
              return (
                <button
                  key={e.codigo}
                  type="button"
                  onClick={() => toggleEmpresa(e.codigo)}
                  className={`rounded-iris-sm border px-2 py-1 text-[11px] transition-colors ${
                    ativa
                      ? 'border-accent bg-accent-soft text-accent-text'
                      : 'border-line bg-surface text-tx-2 hover:bg-surface-2'
                  }`}
                  title={e.razaosocial}
                >
                  <span className="font-mono">{e.codigo}</span>
                  <span className="ml-1">{e.nomeFantasia || e.razaosocial}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="flex-1 overflow-auto rounded-iris-lg border border-line bg-surface shadow-sm">
        {isLoading ? (
          <p className="p-6 text-center text-sm text-tx-3">Carregando metas...</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 p-10 text-center">
            <RectangleStackIcon className="h-10 w-10 text-tx-3" />
            <p className="text-sm text-tx-2">
              Nenhuma meta encontrada com os filtros atuais.
            </p>
            {podeEditar && (
              <button
                type="button"
                onClick={() => abrirCadastro()}
                className="flex items-center gap-1 rounded-iris bg-accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 transition-opacity"
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Cadastrar meta
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-surface-2">
              <tr className="border-b border-line text-left text-tx-2">
                <th className="px-3 py-2 font-medium">Dimensão</th>
                <SortHeader campo="nome" label="Código / Nome" sort={sort} onSort={onSort} />
                <th className="px-3 py-2 font-medium">Descrição</th>
                <th className="px-3 py-2 font-medium">Empresas</th>
                <SortHeader campo="meta" label="Meta" sort={sort} onSort={onSort} alinhar="right" />
                <SortHeader campo="pedido" label="Pedido" sort={sort} onSort={onSort} alinhar="right" />
                <SortHeader campo="cancelado" label="Cancelado" sort={sort} onSort={onSort} alinhar="right" />
                <SortHeader campo="saldo" label="Saldo" sort={sort} onSort={onSort} alinhar="right" />
                <SortHeader campo="percentual" label="%" sort={sort} onSort={onSort} alinhar="right" />
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const cores = corPct(m.percentualAtingido);
                const acoes: AcaoMenu[] = [
                  {
                    key: 'pedidos',
                    label: 'Pedidos',
                    icon: EyeIcon,
                    onClick: () => setDrilldownAlvo({ dimensao: m.dimensao, codigo: m.codigoDimensao, titulo: m.nomeDimensao }),
                  },
                ];
                if (podeEditar) {
                  acoes.push({ key: 'editar', label: 'Editar', icon: PencilSquareIcon, onClick: () => abrirCadastro(m) });
                }
                return (
                  <tr key={m.id} className="border-b border-line last:border-0 hover:bg-surface-2">
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded-full px-2 text-[11px] font-semibold uppercase ${corDimensao(m.dimensao)}`}>
                        {m.dimensao}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="font-mono text-[10px] text-tx-3">{m.codigoDimensao}</span>
                      <div className="text-tx">{m.nomeDimensao}</div>
                    </td>
                    <td className="px-3 py-2 text-tx-2">{m.descricao}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-0.5">
                        {m.empresas.map((cod) => (
                          <span key={cod} className="rounded-iris-sm bg-surface-2 px-1.5 py-0.5 text-[10px] font-mono text-tx-2">
                            {cod}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-medium text-tx">{money(m.valorMeta)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-tx-2">{money(m.valorPedido)}</td>
                    <td className={`whitespace-nowrap px-3 py-2 text-right ${m.valorCancelado > 0 ? 'text-danger' : 'text-tx-3'}`}>
                      {m.valorCancelado > 0 ? `−${money(m.valorCancelado)}` : '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-semibold text-tx">{money(m.valorRealizado)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 text-[11px] font-semibold ${cores.bg} ${cores.text}`}>
                        {m.percentualAtingido.toFixed(1)}%
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <div className="flex justify-end">
                        <MenuAcoes acoes={acoes} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginação */}
      {data && totalCount > 0 && (
        <div className="flex items-center justify-between gap-2 rounded-iris-lg border border-line bg-surface px-3 py-2 text-xs shadow-sm">
          <span className="text-tx-2">
            Mostrando {inicio}–{fim} de {totalCount.toLocaleString('pt-BR')} metas
          </span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setPage(1)} disabled={page <= 1}
              className="rounded-iris-sm border border-line bg-surface px-2 py-1 text-tx-2 hover:bg-surface-2 disabled:opacity-50">
              Primeira
            </button>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
              className="rounded-iris-sm border border-line bg-surface px-2 py-1 text-tx-2 hover:bg-surface-2 disabled:opacity-50">
              Anterior
            </button>
            <span className="px-2 text-tx-2">Página {page} de {totalPages}</span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="rounded-iris-sm border border-line bg-surface px-2 py-1 text-tx-2 hover:bg-surface-2 disabled:opacity-50">
              Próxima
            </button>
            <button type="button" onClick={() => setPage(totalPages)} disabled={page >= totalPages}
              className="rounded-iris-sm border border-line bg-surface px-2 py-1 text-tx-2 hover:bg-surface-2 disabled:opacity-50">
              Última
            </button>
          </div>
        </div>
      )}

      {modalAberta && (
        <ModalMetaCompraDimensao
          meta={metaEditando}
          defaultAnoMes={anoMes}
          defaultEmpresas={empresasSel}
          defaultDimensao={(dimensao || undefined) as DimensaoTipo | undefined}
          onClose={() => { setModalAberta(false); setMetaEditando(null); }}
        />
      )}

      {drilldownAlvo && (
        <MetaCompraDrillDownDrawer
          anoMes={anoMes}
          empresas={empresasSel}
          dimensao={drilldownAlvo.dimensao}
          codigo={drilldownAlvo.codigo}
          titulo={drilldownAlvo.titulo}
          onClose={() => setDrilldownAlvo(null)}
        />
      )}
    </div>
  );
};

export default MetasCompraDimensaoPage;
